import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { isRtlLanguage } from "./i18n";
import Topbar from "./global/Topbar";
import Dashboard from "./screens/dashboard";
import Sidebar from "./global/Sidebar";
import Product from "./screens/categories/product";
import SignIn from "./screens/auth/signin";
import {
  Routes,
  Route,
  useNavigate,
  useLocation,
  Navigate,
} from "react-router-dom";
import { ColorModeContext, useMode } from "./theme";
import { CssBaseline, ThemeProvider } from "@mui/material";
import Category from "./screens/categories/category/viewCategory";
import AddCategory from "./screens/categories/category/addCategory";
import AddProduct from "./screens/categories/product/addProduct";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Ingrediant from "./screens/addons/ingrediants";
import Option from "./screens/addons/option";
import Extra from "./screens/extra/extras";
import Desert from "./screens/extra/desserts";
import History from "./screens/history";
import AddOption from "./screens/addons/option/addOption";
import AddIngrediant from "./screens/addons/ingrediants/addIngrediant";
import ModifyIngrediant from "./screens/addons/ingrediants/modifyIngrediant";
import AddExtra from "./screens/extra/extras/addExtra";
import AddDesert from "./screens/extra/desserts/addDessert";
import ModifyCategory from "./screens/categories/category/modifyCategory";
import ModifyProduct from "./screens/categories/product/modifyProduct";
import ModifyExtra from "./screens/extra/extras/modifyExtra";
import ModifyDesert from "./screens/extra/desserts/modifyDessert";
import ModifyOption from "./screens/addons/option/modifyOption";
import Drink from "./screens/extra/drinks";
import AddDrink from "./screens/extra/drinks/addDrink";
import ModifyDrink from "./screens/extra/drinks/modifyDrink";
import SettingsManagement from "./screens/settings/parameters";
import ModifySettings from "./screens/settings/parameters/modifySettings";
import CarouselMedia from "./screens/settings/carousel";
import AddToCarousel from "./screens/settings/carousel/addToCarousel";
import ModifyCarousel from "./screens/settings/carousel/modifyCarousel";
import Variantion from "./screens/categories/variation";
import ProductByCategory from "./screens/categories/category/viewProduct";
import TypeVariantion from "./screens/categories/variation/typeVariation";
import AddTypeVariation from "./screens/categories/variation/addTypeVariation";
import ModifyTypeVariation from "./screens/categories/variation/modifyTypeVariation";
import AddUser from "./screens/users/addUser";
import Users from "./screens/users";
import ModifyUser from "./screens/users/modifyUser";
import { useAuth } from "./hooks/authHook";
import Restaurant from "./screens/restaurants";
import DelayedNotFound from "./global/delayedNotFound";
import BottomBar from "./global/bottomBar";
import SettingsCurrency from "./screens/settings/currency";
import PrinterManagement from "./screens/settings/printer";
import MailManagement from "./screens/settings/mailing";
import ModifyMail from "./screens/settings/mailing/modifyMail";
import CouponManagement from "./screens/settings/coupon";
import AddCoupon from "./screens/settings/coupon/addCoupon";
import ModifyCoupon from "./screens/settings/coupon/modifyCoupon";
import CurrencyManagement from "./screens/settings/currency/currencyManagement";
import AddCurrency from "./screens/settings/currency/addCurrency";
import ModifyCurrency from "./screens/settings/currency/modifyCurrency";
import AllergyManagement from "./screens/allergy";
import AddAllergy from "./screens/allergy/addAllergy";
import ModifyAllergy from "./screens/allergy/modifyAllergy";
import UserPermissions from "./screens/userPermission";
import AddRestaurant from "./screens/restaurants/addRestaurant";
import ModifyRestaurant from "./screens/restaurants/modifyRestaurant";
import ModifyPrinter from "./screens/settings/printer/modifyPrinter";
import OutterSideBar from "./global/outterSideBar";
import ProtectedRoutes from "./utils/protectedRoutes";
import { useIsAuthenticated } from "./utils/auth/isAuthenticated";
import { useShouldShowLayout } from "./utils/auth/shouldShowLayout";
import { useHasValidRestaurant } from "./utils/auth/hasValidRestaurant";
import { useRestaurant } from "./hooks/RestaurantContext";
import { fetchAuthUser } from "./features/authSlice";
import Loading from "./components/loading";
import Media from "./screens/media";
import AddMedia from "./screens/media/addMedia";
import ModifyMedia from "./screens/media/modifyMedia";
import AddUserFromPermission from "./screens/userPermission/addUserFromPermission";
import ModifyUserFromPermission from "./screens/userPermission/modifyUserFromPermission";

function App() {
  const dispatch = useDispatch();
  const isAuthenticated = useIsAuthenticated();
  const shouldShowLayout = useShouldShowLayout();
  const hasValidRestaurant = useHasValidRestaurant();
  const navigate = useNavigate();
  const location = useLocation();
  const { role, assignedRestaurants, authChecked } = useAuth();
  const { setRestaurantId } = useRestaurant();
  const { i18n } = useTranslation();
  const direction = isRtlLanguage(i18n.language) ? "rtl" : "ltr";
  const { theme, colorMode } = useMode(direction);
  const layoutClass = shouldShowLayout ? "app-content app" : "app-signin app";

  useEffect(() => {
    document.documentElement.dir = direction;
    document.documentElement.lang = i18n.language.split("-")[0];
  }, [direction, i18n.language]);

  // Restore the session on first load: the httpOnly cookie can't be read by
  // JS, so the only way to know "am I logged in" is to ask the server.
  useEffect(() => {
    dispatch(fetchAuthUser());
  }, [dispatch]);

  useEffect(() => {
    if (!authChecked) return;
    if (!isAuthenticated && location.pathname !== "/signin") {
      navigate("/signin");
    } else if (isAuthenticated && location.pathname === "/signin") {
      if (hasValidRestaurant) {
        navigate("/dashboard");
      } else if (role === "admin") {
        navigate("/restaurants");
      } else if (role === "manager") {
        if (assignedRestaurants.length === 1) {
          setRestaurantId(assignedRestaurants[0].id);
          navigate("/dashboard");
        } else {
          navigate("/restaurants");
        }
      }
    }
  }, [
    authChecked,
    isAuthenticated,
    location.pathname,
    navigate,
    role,
    assignedRestaurants,
    hasValidRestaurant,
    setRestaurantId,
  ]);

  useEffect(() => {
    if (!authChecked) return;
    if (
      isAuthenticated &&
      hasValidRestaurant &&
      ["/restaurants", "/signin", "/"].includes(location.pathname)
    ) {
      navigate("/dashboard");
    }
  }, [authChecked, isAuthenticated, hasValidRestaurant, location.pathname, navigate]);

  useEffect(() => {
    if (!authChecked) return;
    if (
      isAuthenticated &&
      !hasValidRestaurant &&
      role !== "admin" &&
      ![
        "/restaurants",
        "/restaurants/add",
        "/restaurants/modify",
        "/signin",
      ].includes(location.pathname)
    ) {
      navigate("/restaurants");
    }
  }, [authChecked, isAuthenticated, location.pathname, hasValidRestaurant, navigate, role]);

  if (!authChecked) {
    return (
      <ColorModeContext.Provider value={colorMode}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Loading fullPage />
        </ThemeProvider>
      </ColorModeContext.Provider>
    );
  }


  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <div className={layoutClass}>
          {shouldShowLayout && hasValidRestaurant && <Sidebar />}
          {shouldShowLayout && !hasValidRestaurant && role === "admin" && (
            <OutterSideBar />
          )}
          {shouldShowLayout && hasValidRestaurant && <Topbar />}
          <Routes>
            <Route path="/signin" element={<SignIn />} />
            <Route element={<ProtectedRoutes />}>
              {hasValidRestaurant && (
                <>
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/category">
                    <Route index element={<Category />} />
                    <Route path="add" element={<AddCategory />} />
                    <Route path="modify" element={<ModifyCategory />} />
                    <Route path="productsByCategory" element={<ProductByCategory />} />
                  </Route>
                  <Route path="/products">
                    <Route index element={<Product />} />
                    <Route path="add" element={<AddProduct />} />
                    <Route path="modify" element={<ModifyProduct />} />
                  </Route>
                  <Route path="/variation" element={<Variantion />} />
                  <Route path="/typeVariation">
                    <Route index element={<TypeVariantion />} />
                    <Route path="add" element={<AddTypeVariation />} />
                    <Route path="modify" element={<ModifyTypeVariation />} />
                  </Route>
                  <Route path="/ingrediants">
                    <Route index element={<Ingrediant />} />
                    <Route path="add" element={<AddIngrediant />} />
                    <Route path="modify" element={<ModifyIngrediant />} />
                  </Route>
                  <Route path="/options">
                    <Route index element={<Option />} />
                    <Route path="add" element={<AddOption />} />
                    <Route path="modify" element={<ModifyOption />} />
                  </Route>
                  <Route path="/extra">
                    <Route index element={<Extra />} />
                    <Route path="add" element={<AddExtra />} />
                    <Route path="modify" element={<ModifyExtra />} />
                  </Route>
                  <Route path="/drink">
                    <Route index element={<Drink />} />
                    <Route path="add" element={<AddDrink />} />
                    <Route path="modify" element={<ModifyDrink />} />
                  </Route>
                  <Route path="/desert">
                    <Route index element={<Desert />} />
                    <Route path="add" element={<AddDesert />} />
                    <Route path="modify" element={<ModifyDesert />} />
                  </Route>
                  <Route path="/users">
                    <Route index element={<Users />} />
                    <Route path="add" element={<AddUser />} />
                    <Route path="modify" element={<ModifyUser />} />
                  </Route>
                  <Route path="/history" element={<History />} />
                  <Route path="/settings">
                    <Route index element={<SettingsManagement />} />
                    <Route path="modify" element={<ModifySettings />} />
                  </Route>
                  <Route path="/carousel">
                    <Route index element={<CarouselMedia />} />
                    <Route path="add" element={<AddToCarousel />} />
                    <Route path="modify" element={<ModifyCarousel />} />
                  </Route>
                  <Route path="/currency" element={<SettingsCurrency />} />
                  <Route path="/printer">
                    <Route index element={<PrinterManagement />} />
                    <Route path="modify" element={<ModifyPrinter />} />
                  </Route>
                  <Route path="/mailing">
                    <Route index element={<MailManagement />} />
                    <Route path="modify" element={<ModifyMail />} />
                  </Route>
                  <Route path="/coupon">
                    <Route index element={<CouponManagement />} />
                    <Route path="add" element={<AddCoupon />} />
                    <Route path="modify" element={<ModifyCoupon />} />
                  </Route>
                </>
              )}
              {!hasValidRestaurant && role === "admin" && (
                <>
                  <Route path="/restaurants">
                    <Route index element={<Restaurant />} />
                    <Route path="add" element={<AddRestaurant />} />
                    <Route path="modify" element={<ModifyRestaurant />} />
                  </Route>
                  <Route path="/currency-management">
                    <Route index element={<CurrencyManagement />} />
                    <Route path="add" element={<AddCurrency />} />
                    <Route path="modify" element={<ModifyCurrency />} />
                  </Route>
                  <Route path="/allergy">
                    <Route index element={<AllergyManagement />} />
                    <Route path="add" element={<AddAllergy />} />
                    <Route path="modify" element={<ModifyAllergy />} />
                  </Route>
                  <Route path="/user-permissions">
                    <Route index element={<UserPermissions />} />
                    <Route path="add" element={<AddUserFromPermission />} />
                    <Route path="modify" element={<ModifyUserFromPermission />} />
                  </Route>
                  <Route path="/media">
                    <Route index element={<Media />} />
                    <Route path="add" element={<AddMedia />} />
                    <Route path="modify" element={<ModifyMedia />} />
                  </Route>
                </>
              )}
              {!hasValidRestaurant && role === "manager" && assignedRestaurants && assignedRestaurants.length > 1 && (
                <Route path="/restaurants" element={<Restaurant />} />
              )}
            </Route>
            <Route
              path="/"
              element={
                isAuthenticated ? (
                  <Navigate to={hasValidRestaurant ? "/dashboard" : "/restaurants"} />
                ) : (
                  <Navigate to="/signin" />
                )
              }
            />
            <Route path="*" element={<DelayedNotFound />} />
          </Routes>
          {isAuthenticated && <BottomBar />}
          <ToastContainer
            position="top-right"
            autoClose={5000}
            hideProgressBar={false}
            closeOnClick={true}
            pauseOnHover={true}
            draggable={true}
            progress={undefined}
            theme="colored"
          />
        </div>
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

export default App;
